import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ConfirmCancelDialog } from '../../../../../common/confirm-cancel-dialog/confirm-cancel-dialog';
import { AssignService } from '../../../../../../services/assign-faculty.service';

@Component({
  selector: 'app-assign-dialog',
  imports: [ReactiveFormsModule, MatCardModule, MatFormFieldModule, MatIconModule, MatButtonModule],
  templateUrl: './assign-dialog.html',
  styleUrl: './assign-dialog.css',
})
export class AssignDialog {
  dialogRef = inject(MatDialogRef<AssignDialog>);
  data = inject<{} | null>(MAT_DIALOG_DATA) ?? {};
  assignService = inject(AssignService);

  private dialog = inject(MatDialog);

  private readonly MAX_CSV_SIZE_BYTES = 10 * 1024 * 1024;

  form = new FormGroup({
    csvFile: new FormControl<File | null>(null, { validators: Validators.required }),
  });

  onCsvAdded(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.form.controls.csvFile.setValue(null);
      return;
    }

    const isCsv =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLowerCase().endsWith('.csv');

    if (!isCsv) {
      this.form.controls.csvFile.setValue(null);
      this.form.controls.csvFile.setErrors({ invalidType: true });
      input.value = '';
      return;
    }

    if (file.size > this.MAX_CSV_SIZE_BYTES) {
      this.form.controls.csvFile.setValue(null);
      this.form.controls.csvFile.setErrors({ tooLarge: true });
      input.value = '';
      return;
    }

    this.form.controls.csvFile.setValue(file);
    this.form.controls.csvFile.markAsTouched();
  }

  assign() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmCancelDialog, {
      data: {
        title: 'Bulk assign students to staff',
        content:
          'This will assign students to staff members based on the uploaded CSV. Are you sure you want to continue?',
        confirmLabel: 'Assign',
        icon: 'group_add',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performAssignment();
      }
    });
  }

  private performAssignment() {
    const raw = this.form.getRawValue();
    if (!raw.csvFile) {
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', raw.csvFile, raw.csvFile.name);

    this.assignService.bulkAssignStudents(formData).subscribe({
      next: (result) => {
        this.dialogRef.close({ data: result });
      },
      error: (err) => {
        console.error('bulkAssignStudents failed:', err);
      },
    });
  }
}
